import React, { useMemo, useState } from 'react';
import {
  ChevronRight,
  ChevronLeft,
  BarChart3,
  CheckCircle,
  Clock,
  AlertCircle,
  BookOpen,
} from 'lucide-react';
import type { ModuleElement } from '@/types/filiere.types';

interface SchedulingProgressPanelProps {
  matieres: ModuleElement[];
  scheduledHoursMap: Map<string, number>;
  onMatiereClick?: (matiere: ModuleElement) => void;
  groupName?: string;
}

type ScheduleStatus = 'NOT_STARTED' | 'PARTIAL' | 'COMPLETE';

interface ModuleProgress {
  matiere: ModuleElement;
  volumeHoraire: number;
  scheduledHours: number;
  status: ScheduleStatus;
  percentage: number;
}

export const SchedulingProgressPanel: React.FC<SchedulingProgressPanelProps> = ({
  matieres,
  scheduledHoursMap,
  onMatiereClick,
  groupName,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // Calculate progress for each module
  const moduleProgress = useMemo((): ModuleProgress[] => {
    return matieres.map((matiere) => {
      const volumeHoraire = matiere.volumeHoraire || 0;
      const scheduledHours = scheduledHoursMap.get(matiere.id) || 0;

      let status: ScheduleStatus = 'NOT_STARTED';
      let percentage = 0;

      if (volumeHoraire > 0) {
        percentage = Math.min(100, (scheduledHours / volumeHoraire) * 100);
        if (scheduledHours >= volumeHoraire) {
          status = 'COMPLETE';
        } else if (scheduledHours > 0) {
          status = 'PARTIAL';
        }
      } else if (scheduledHours > 0) {
        // No volume defined but has scheduled hours
        status = 'PARTIAL';
        percentage = 50; // Arbitrary since no target
      }

      return {
        matiere,
        volumeHoraire,
        scheduledHours,
        status,
        percentage,
      };
    });
  }, [matieres, scheduledHoursMap]);

  // Calculate summary stats
  const summary = useMemo(() => {
    const totalModules = moduleProgress.length;
    const modulesComplete = moduleProgress.filter((m) => m.status === 'COMPLETE').length;
    const modulesPartial = moduleProgress.filter((m) => m.status === 'PARTIAL').length;
    const modulesNotStarted = moduleProgress.filter((m) => m.status === 'NOT_STARTED').length;

    const totalHoursRequired = moduleProgress.reduce((sum, m) => sum + m.volumeHoraire, 0);
    const totalHoursScheduled = moduleProgress.reduce((sum, m) => sum + m.scheduledHours, 0);

    const overallPercentage =
      totalHoursRequired > 0 ? Math.min(100, (totalHoursScheduled / totalHoursRequired) * 100) : 0;

    return {
      totalModules,
      modulesComplete,
      modulesPartial,
      modulesNotStarted,
      totalHoursRequired,
      totalHoursScheduled,
      overallPercentage,
    };
  }, [moduleProgress]);

  const getStatusIcon = (status: ScheduleStatus) => {
    switch (status) {
      case 'COMPLETE':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'PARTIAL':
        return <Clock className="w-4 h-4 text-amber-500" />;
      case 'NOT_STARTED':
        return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: ScheduleStatus) => {
    switch (status) {
      case 'COMPLETE':
        return 'bg-green-500';
      case 'PARTIAL':
        return 'bg-amber-500';
      case 'NOT_STARTED':
        return 'bg-gray-300';
    }
  };

  if (!isExpanded) {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        className="fixed right-0 top-1/2 -translate-y-1/2 bg-white border border-gray-200 rounded-l-lg shadow-lg p-2 hover:bg-gray-50 transition-colors z-40"
        title="Ouvrir le panneau de progression"
      >
        <div className="flex flex-col items-center gap-2">
          <ChevronLeft className="w-5 h-5 text-gray-600" />
          <BarChart3 className="w-5 h-5 text-primary-600" />
          <span className="text-xs font-medium text-gray-600 writing-mode-vertical">
            Progression
          </span>
        </div>
      </button>
    );
  }

  return (
    <div className="w-80 bg-white border-l border-gray-200 flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-primary-600" />
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Progression</h3>
            {groupName && <p className="text-xs text-gray-500">{groupName}</p>}
          </div>
        </div>
        <button
          onClick={() => setIsExpanded(false)}
          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          title="Réduire le panneau"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Summary Cards */}
      <div className="px-4 py-3 border-b border-gray-100 space-y-3">
        {/* Overall Progress */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-gray-600">Progression globale</span>
            <span className="text-xs font-semibold text-gray-900">
              {Math.round(summary.overallPercentage)}%
            </span>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-600 rounded-full transition-all duration-300"
              style={{ width: `${summary.overallPercentage}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {Math.round(summary.totalHoursScheduled)}h / {summary.totalHoursRequired}h planifiées
          </p>
        </div>

        {/* Status Summary */}
        <div className="grid grid-cols-3 gap-2">
          <div className="text-center p-2 bg-green-50 rounded-lg">
            <p className="text-lg font-bold text-green-700">{summary.modulesComplete}</p>
            <p className="text-xs text-green-600">Complet</p>
          </div>
          <div className="text-center p-2 bg-amber-50 rounded-lg">
            <p className="text-lg font-bold text-amber-700">{summary.modulesPartial}</p>
            <p className="text-xs text-amber-600">Partiel</p>
          </div>
          <div className="text-center p-2 bg-gray-50 rounded-lg">
            <p className="text-lg font-bold text-gray-700">{summary.modulesNotStarted}</p>
            <p className="text-xs text-gray-600">Non planifié</p>
          </div>
        </div>
      </div>

      {/* Module List */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Matières ({summary.totalModules})
          </p>
        </div>
        <div className="divide-y divide-gray-100">
          {moduleProgress.map((item) => (
            <button
              key={item.matiere.id}
              onClick={() => onMatiereClick?.(item.matiere)}
              className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-start gap-3">
                {getStatusIcon(item.status)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {item.matiere.name}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-gray-500">
                      {Math.round(item.scheduledHours)}/{item.volumeHoraire}h planifiées
                    </span>
                  </div>
                  {/* Mini progress bar */}
                  <div className="w-full h-1 bg-gray-200 rounded-full mt-2 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${getStatusColor(item.status)}`}
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                </div>
              </div>
            </button>
          ))}

          {moduleProgress.length === 0 && (
            <div className="px-4 py-8 text-center">
              <BookOpen className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">Aucune matière à planifier</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SchedulingProgressPanel;
